import { useMemo } from "react";
import {
  useAssignLabel,
  useCreateLabel,
  useLabelsQuery,
  useUnassignLabel,
} from "@/hooks/useLeadLabels";
import { ManageLabelsModal } from "@/components/ManageLabelsModal";
import { LabelsContext, type LabelsContextValue } from "./labels-context";

/**
 * Holds the label query and the assign/unassign/create mutations ONCE for every
 * card below it.
 *
 * Before this, `CardLabels` called those four hooks itself. On a board of 478
 * leads that meant 478 query observers on the same `["labels"]` key plus ~1,400
 * mutation observers, and TanStack notifies every observer on every mutation —
 * so each label toggle or stage move did O(cards) work and the board crawled.
 *
 * It also mounts `ManageLabelsModal` once, here. Every "Administrar etiquetas"
 * trigger lives in a `CardLabels` dropdown or a page toolbar below this
 * provider, so this is the one place that guarantees the modal exists wherever
 * something can open it.
 */
export function LabelsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const { data } = useLabelsQuery();
  const assignLabel = useAssignLabel();
  const unassignLabel = useUnassignLabel();
  const createLabel = useCreateLabel();

  // Stable identity: `data ?? []` inline would mint a new array every render,
  // changing the context value and re-rendering every card below.
  const allLabels = useMemo(() => data ?? [], [data]);
  const assignMutate = assignLabel.mutate;
  const unassignMutate = unassignLabel.mutate;
  const createMutate = createLabel.mutate;
  const isCreating = createLabel.isPending;

  const value = useMemo<LabelsContextValue>(
    () => ({
      allLabels,
      assign: (leadId, labelId) => assignMutate({ leadId, labelId }),
      unassign: (leadId, labelId) => unassignMutate({ leadId, labelId }),
      create: (input, onCreated) => createMutate(input, { onSuccess: onCreated }),
      isCreating,
    }),
    [allLabels, assignMutate, unassignMutate, createMutate, isCreating],
  );

  return (
    <LabelsContext.Provider value={value}>
      {children}
      <ManageLabelsModal />
    </LabelsContext.Provider>
  );
}
